import React from 'react';
import VisaPageLayout2, { FaqItem, ProcessStep, RequirementItem, VisaCategory, VisaPlan } from './visa-page-layout-2';


const categories: VisaCategory[] = [
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z" /><circle cx="12" cy="12" r="3" /></svg>),
    title: 'Tourism & Visit',
    text: 'Sightseeing, holidays, or visiting friends and family across the Schengen Area.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>),
    title: 'Business',
    text: 'Meetings, conferences, trade fairs, and other short-term professional visits.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v6" /><path d="M5 22V12a7 7 0 0 1 14 0v10" /><path d="M3 22h18" /></svg>),
    title: 'Airport Transit',
    text: 'Passing through a Schengen airport on the way to a non-Schengen destination.',
  },
];

const plans: VisaPlan[] = [
  { id: 'sch-single', name: 'Short-Stay Visa (Type C)', entryType: 'Single Entry', validity: 'Valid for the dates on your itinerary', stay: 'Up to 90 days within a 180-day period', processing: '10–15 business days', price: '150', currency: 'OMR' },
  { id: 'sch-multi', name: 'Multiple-Entry Visa (Type C)', entryType: 'Multiple Entry', validity: 'Valid for 1 year from issue', stay: 'Up to 90 days per 180-day period', processing: '15–20 business days', price: '210', currency: 'OMR', featured: true },
  { id: 'sch-priority', name: 'Priority Appointment Service', entryType: 'Single or Multiple Entry', validity: 'As approved by the consulate', stay: 'As approved by the consulate', processing: 'Expedited where available', price: '280', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Completed application form', detail: 'Signed Schengen visa application form, one per traveler including children.' },
  { label: 'Passport', detail: 'Valid for at least 3 months beyond your planned departure from the Schengen Area, with 2 blank pages.' },
  { label: 'Passport-size photos', detail: 'Two recent photos meeting Schengen biometric photo standards.' },
  { label: 'Travel insurance', detail: 'Minimum €30,000 medical coverage valid across all Schengen countries for your full trip.' },
  { label: 'Flight itinerary', detail: 'Round-trip reservation — not necessarily a paid ticket, but a confirmed booking.' },
  { label: 'Accommodation proof', detail: 'Hotel bookings or an invitation letter for the entire length of stay.' },
  { label: 'Proof of funds', detail: 'Bank statements showing sufficient funds for your trip, per the destination country\'s guidelines.' },
  { label: 'Employment or enrollment letter', detail: 'A letter confirming your job or studies, and approved leave dates where applicable.' },
];

const goodToKnow: string[] = [
  'You apply at the embassy or consulate of your main destination — or your first port of entry if you\'re visiting several countries.',
  'Biometric data (fingerprints and a photo) is collected in person at the visa application centre, usually on your first Schengen application and every 5 years after.',
  'A visa doesn\'t guarantee entry — border officers still check your documents on arrival, so we make sure everything is consistent and well-organized.',
  'Apply no earlier than 6 months, and no later than 15 working days, before your trip — we\'ll help you time it right.',
];

const steps: ProcessStep[] = [
  { title: 'Consultation & document review', text: 'We confirm the right visa category and destination country, then walk you through the full checklist.' },
  { title: 'Application preparation', text: 'Our team compiles your file — forms, insurance, itinerary and financial documents — ready for submission.' },
  { title: 'Appointment booking', text: 'We book your biometric appointment at the relevant embassy, consulate, or visa application centre.' },
  { title: 'Submission & biometrics', text: 'You attend your appointment; we brief you beforehand on what to expect and bring.' },
  { title: 'Decision & passport return', text: 'We track your application status and notify you the moment a decision is made.' },
];

const faqs: FaqItem[] = [
  { question: 'Which country\'s embassy should I apply to?', answer: 'You apply to the embassy of the country where you\'ll spend the most time, or your first entry point if your stays are equal. We\'ll confirm the right one based on your itinerary.' },
  { question: 'Can one Schengen visa cover multiple countries?', answer: 'Yes — a Schengen visa lets you travel freely across all Schengen member states during its validity, regardless of which embassy issued it.' },
  { question: 'What happens if my visa is refused?', answer: 'Refusals do happen and usually come with a stated reason. We review the notice with you and advise whether a fresh application addressing the concern makes sense.' },
  { question: 'How far in advance should I start the process?', answer: 'We recommend starting at least 4–6 weeks before travel, since appointment availability at some consulates can be limited, especially in peak season.' },
  { question: 'Do children need their own visa and appointment?', answer: 'Yes, every traveler needs an individual application, though young children are sometimes exempt from providing fingerprints — we\'ll confirm the rule for your destination.' },
];

export default function VisaSchengenPage() {
  return (
    <VisaPageLayout2
      countryName="Schengen Area"
      flagEmoji="🇪🇺"
      heroEyebrow="Schengen Area — 29 European Countries"
      heroImage="https://images.unsplash.com/photo-1708663036164-2d2818fad011?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Schengen"
      heroTitleTail="visa, prepared with precision"
      heroSubtitle="One visa, 29 countries. From Paris to Prague, we prepare your Schengen application with the accuracy this process demands — because a small oversight here can cost you the whole trip."
      trustStats={[
        { value: '10–20', label: 'Business Days' },
        { value: '93%', label: 'Approval Rate' },
        { value: '2,050+', label: 'Schengen Visas Issued' },
      ]}
      categories={categories}
      plans={plans}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      useTimeline
      faqs={faqs}
      ctaSubtitle="Book a consultation and we'll map out exactly which embassy to apply to, what to prepare, and when — before you commit to any appointment."
    />
  );
}